import { FormHelperText, Typography } from '@mui/material';
import { FieldArray, Form, Formik, type FormikHelpers } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { array, number, object, string } from 'yup';

import { BLOOD_PRESSURE } from '@constants/fhir';

import { useParameter } from '@hooks/useParameter';
import { useSession } from '@hooks/useSession';

import { FRIENDLY_CODE_NAME_MAP } from '@models/graph';
import {
  type FormValues,
  ParameterExpression,
  type ParameterValue,
} from '@models/reference-range';

import type { ReferenceRangeRiskLevel } from 'generated/types';

import BoundForm from './BoundForm';
import Footer from './Footer';

const messages = defineMessages({
  title: {
    id: 'healthTracker.parameter.title',
    defaultMessage: '{code} Parameters',
  },
  required: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  noChanges: {
    id: 'GLOBAL.noChanges',
    defaultMessage: 'No changes were made',
  },
});

const { Between, Gt, Gte, Lt, Lte } = ParameterExpression;

interface ParameterFormProps {
  risk: ReferenceRangeRiskLevel;
  values: ParameterValue[];
}

export default function ParameterForm({
  risk,
  values: formValues,
}: ParameterFormProps) {
  const { formatMessage } = useIntl();
  const { code, formRefMap } = useParameter();
  const {
    state: { activeProfile },
  } = useSession();

  const ref = formRefMap.get(risk);
  const isBloodPressure = code === BLOOD_PRESSURE;

  const initialValues: FormValues = {
    parameters: formValues,
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    parameters: array()
      .of(
        object().shape({
          parameter_id: string().required(formatMessage(messages.required)),
          code: string().required(formatMessage(messages.required)),
          vitall_patient_id: string().nullable(),
          lower_bound: number().when('expression', {
            is: (value: ParameterExpression) =>
              value === Gt || value === Gte || value === Between,
            then: () =>
              number().min(0).required(formatMessage(messages.required)),
            otherwise: () => number().min(0).nullable(),
          }),
          upper_bound: number().when('expression', {
            is: (value: ParameterExpression) =>
              value === Lt || value === Lte || value === Between,
            then: () =>
              number().min(0).required(formatMessage(messages.required)),
            otherwise: () => number().min(0).nullable(),
          }),
          expression: string()
            .oneOf([Between, Gt, Gte, Lt, Lte])
            .required(formatMessage(messages.required)),
        })
      )
      .required(),
  });

  const handleSubmit = (
    _values: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) => {
    if (!activeProfile?.vitall_patient_id) {
      setErrors({ submit: 'No vitall patient' });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      innerRef={ref}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ errors, values }) => {
        const orderedParameters = isBloodPressure
          ? // Ensure systolic appears before diastolic
            values.parameters.sort((a, b) => b.code.localeCompare(a.code))
          : values.parameters;

        return (
          <Form>
            <FieldArray
              name="parameters"
              render={(_arrayHelpers) => (
                <>
                  {orderedParameters.map((param, paramIndex) => {
                    const title =
                      code !== param.code ? (
                        <Typography color="primary.main" variant="body2">
                          {FRIENDLY_CODE_NAME_MAP[param.code]}
                        </Typography>
                      ) : null;
                    return (
                      <React.Fragment
                        key={`parameter-${param.code}-${param.parameter_id}`}
                      >
                        {title}
                        <BoundForm index={paramIndex} parameter={param} />
                      </React.Fragment>
                    );
                  })}
                  {Boolean(errors.submit) && (
                    <FormHelperText
                      sx={({ palette }) => ({ color: palette.error.main })}
                    >
                      {errors.submit}
                    </FormHelperText>
                  )}
                  <Footer parameters={values.parameters} />
                </>
              )}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
